import { inspectMessage } from "@chatbotgang/etude/debug/inspectMessage";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useEffect } from "react";

import { ResourceNotFoundError } from "@/features/error";
import { interlude } from "@/interlude";
import {
  ChannelQueriesContext,
  useChannelQueries,
} from "@/pages/channels/pages/detail/channelQueriesContext/context";

const ChannelQueriesContextProviderById: React.FC<{
  channelId: number;
  children: React.ReactNode;
}> = ({ channelId, children }) => {
  const queries = useChannelQueries(channelId);
  return (
    <ChannelQueriesContext.Provider queries={queries}>
      {children}
    </ChannelQueriesContext.Provider>
  );
};

const ChannelQueriesContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const params = useParams({ strict: false });
  const id = params.id;
  const navigate = useNavigate();

  const channelsQuery = interlude.channel.useChannels(undefined, {
    enabled: typeof id === "string",
    select(data) {
      return data.channels;
    },
  });

  useEffect(
    function navigateChannelUuid() {
      if (typeof id === "number") return;
      if (!channelsQuery.isSuccess) return;

      const channel = channelsQuery.data.find((channel) => channel.uuid === id);
      if (!channel) {
        throw new ResourceNotFoundError(
          inspectMessage`Channel with uuid ${id} not found`,
        );
      }
      navigate({ to: `/channels/${channel.type}/${channel.id}` });
    },
    [channelsQuery.isSuccess, channelsQuery.data, id, navigate],
  );

  if (typeof id !== "number") return null;

  return (
    <ChannelQueriesContextProviderById channelId={id}>
      {children}
    </ChannelQueriesContextProviderById>
  );
};

export { ChannelQueriesContextProvider, ChannelQueriesContextProviderById };
